/**
 * Authenticated standing-membership payment screen (`/mensalistas/:id`).
 * Shows the membership and an "outstanding payment" block for the open
 * monthly charges — payable in-app via the dynamic Asaas Pix QR when the
 * arena has `pixOnline`; arenas without it render "pague na arena" (no
 * static key on the arena side — product decision, epic #66).
 *
 * There is no detail endpoint: the list is small, so the page loads it and
 * picks the membership by id (robust on refresh). The outstanding block comes
 * embedded and is then polled until it empties out. Compliance: every
 * payer-facing Pix block carries the Asaas seal + processed-by clause.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { AsaasSeal, Body, Button, ButtonVariant, Caption, H4 } from '@meiuca/ui-components'
import { AlertCircle, ArrowLeft, Loader2, Store } from 'lucide-react'
import { NotFoundError } from '@meiuca/service'
import { formatCurrency, formatDateForUI, getDayOfWeekName, parseAPIDate } from '@meiuca/web-foundation'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { PixPayment } from '@/components/PixPayment'
import { useAuth } from '@/contexts/AuthContext'
import { toastManager } from '@/lib/toastManager'
import {
  venueMembershipService,
  type VenueMembershipCycleItem,
  type VenueMembershipCycleStatus,
  type VenueMembershipItem,
  type VenueMembershipOutstandingPaymentItem
} from '@/services/venueMembershipService'

const POLL_INTERVAL_MS = 4000

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

function formatCycleMonth(year: number, month: number): string {
  return MONTHS_PT[month - 1] ? `${MONTHS_PT[month - 1]} de ${year}` : `${month}/${year}`
}

const CYCLE_BADGES: Record<VenueMembershipCycleStatus, { text: string; color: string; dot: string }> = {
  paid: { text: 'Pago', color: 'bg-feedback-success/20 text-feedback-success', dot: 'bg-feedback-success' },
  pending: { text: 'Pendente', color: 'bg-feedback-warning/20 text-feedback-warning', dot: 'bg-feedback-warning' },
  failed: { text: 'Falhou', color: 'bg-feedback-error/20 text-feedback-error', dot: 'bg-feedback-error' },
  canceled: { text: 'Cancelado', color: 'bg-background-secondary text-text-secondary', dot: 'bg-text-tertiary' }
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error' }
  | { kind: 'ok'; membership: VenueMembershipItem }

export function MembershipDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [outstanding, setOutstanding] = useState<VenueMembershipOutstandingPaymentItem[]>([])
  const [cycles, setCycles] = useState<VenueMembershipCycleItem[] | null>(null)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const outstandingIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    document.title = 'Horário fixo · Meiuca'
    analyticsService.trackScreenView(SCREEN_NAMES.MEMBERSHIP_DETAIL)
  }, [])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  const startPolling = useCallback(() => {
    if (!id) return
    stopPolling()
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await venueMembershipService.listOutstandingPayments(id)
        const freshIds = new Set(fresh.map((o) => o.id))
        const paidSomething = Array.from(outstandingIdsRef.current).some((oldId) => !freshIds.has(oldId))

        setOutstanding(fresh)
        outstandingIdsRef.current = freshIds

        if (paidSomething) {
          analyticsService.track(ANALYTICS_EVENTS.MEMBERSHIP_PAYMENT_COMPLETED, { membership_id: id })
          toastManager.success('Pagamento confirmado!')
          venueMembershipService.listCycles(id).then(setCycles).catch(() => undefined)
        }
        if (fresh.length === 0) {
          stopPolling()
        }
      } catch {
        // Transient error while polling — keep trying on the next tick.
      }
    }, POLL_INTERVAL_MS)
  }, [id, stopPolling])

  const load = useCallback(async () => {
    if (!id) {
      setState({ kind: 'not-found' })
      return
    }
    try {
      // Active first (the common case); ended ones live in the history slice.
      let membership = (await venueMembershipService.listMemberships('active')).find((m) => m.id === id)
      if (!membership) {
        membership = (await venueMembershipService.listMemberships('ended')).find((m) => m.id === id)
      }
      if (!membership) {
        setState({ kind: 'not-found' })
        return
      }
      setState({ kind: 'ok', membership })
      venueMembershipService
        .listCycles(id)
        .then(setCycles)
        .catch(() => setCycles([]))

      const embedded = membership.outstandingPayments ?? []
      setOutstanding(embedded)
      outstandingIdsRef.current = new Set(embedded.map((o) => o.id))
      if (embedded.length > 0) {
        startPolling()
      } else {
        stopPolling()
      }
    } catch (err) {
      setState({ kind: err instanceof NotFoundError ? 'not-found' : 'error' })
    }
  }, [id, startPolling, stopPolling])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    load()
  }, [isAuthenticated, load, navigate])

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate('/mensalistas')}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Meus horários fixos
        </button>

        {state.kind === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando...</Body>
          </div>
        )}

        {state.kind === 'not-found' && (
          <EmptyCard
            title="Horário fixo não encontrado"
            message="O link pode estar incorreto ou a mensalidade não está mais ativa."
          />
        )}

        {state.kind === 'error' && (
          <EmptyCard title="Algo deu errado" message="Não conseguimos carregar agora." onRetry={load} />
        )}

        {state.kind === 'ok' && (
          <div className="space-y-4">
            <MembershipHeader membership={state.membership} />

            {outstanding.length > 0 ? (
              <OutstandingPaymentSection>
                {outstanding.map((item) => (
                  <OutstandingPaymentRow key={item.id} item={item} venueName={state.membership.venueName} />
                ))}
              </OutstandingPaymentSection>
            ) : (
              <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light text-center">
                <Body className="text-text-primary font-medium m-0">Tudo em dia</Body>
                <Caption className="text-text-secondary">Nenhuma mensalidade em aberto.</Caption>
              </div>
            )}

            <CyclesHistorySection cycles={cycles} />
          </div>
        )}
      </div>
    </div>
  )
}

/** "Mensalidades": every cycle of the membership, newest first. */
function CyclesHistorySection({ cycles }: { cycles: VenueMembershipCycleItem[] | null }) {
  return (
    <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light">
      <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">Mensalidades</h2>
      {cycles === null ? (
        <Caption className="text-text-secondary">Carregando...</Caption>
      ) : cycles.length === 0 ? (
        <Caption className="text-text-secondary">Nenhuma mensalidade registrada.</Caption>
      ) : (
        <div className="space-y-3">
          {cycles.map((cycle) => (
            <CycleHistoryRow key={cycle.id} cycle={cycle} />
          ))}
        </div>
      )}
    </div>
  )
}

function CycleHistoryRow({ cycle }: { cycle: VenueMembershipCycleItem }) {
  const badge = CYCLE_BADGES[cycle.status] ?? CYCLE_BADGES.pending
  const detail =
    cycle.status === 'paid' && cycle.paidAt
      ? `Pago em ${formatDateForUI(parseAPIDate(cycle.paidAt), 'medium')}`
      : cycle.dueAt
        ? `Vencimento ${formatDateForUI(parseAPIDate(cycle.dueAt), 'medium')}`
        : null

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border-light p-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${badge.dot}`} />
        <div className="min-w-0">
          <Body className="text-sm font-medium text-text-primary m-0">
            {formatCycleMonth(cycle.cycleYear, cycle.cycleMonth)}
          </Body>
          <Caption className="text-text-secondary">
            {formatCurrency(cycle.totalAmountCents)}
            {detail ? ` · ${detail}` : ''}
          </Caption>
        </div>
      </div>
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>{badge.text}</span>
    </div>
  )
}

function isEnded(membership: VenueMembershipItem): boolean {
  return !!membership.endDate && parseAPIDate(membership.endDate).getTime() < Date.now()
}

function MembershipHeader({ membership }: { membership: VenueMembershipItem }) {
  return (
    <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <H4 className="text-text-primary">{membership.venueName}</H4>
          <Caption className="text-text-secondary">
            Toda {getDayOfWeekName(membership.weekday, 'long').toLowerCase()}
          </Caption>
        </div>
        {isEnded(membership) ? (
          <span className="px-2 py-1 rounded text-xs font-medium bg-background-secondary text-text-secondary">
            Finalizado
          </span>
        ) : (
          <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-success/20 text-feedback-success">
            Ativo
          </span>
        )}
      </div>
      <div className="flex items-end justify-between pt-3 border-t border-border-light">
        <div>
          <Body className="text-2xl font-bold text-brand-primary m-0">
            {formatCurrency(membership.totalAmountCents)}
          </Body>
          <Caption className="text-text-secondary">por mês</Caption>
        </div>
        {membership.dueAt && (
          <Caption className="text-text-secondary">
            Vence {formatDateForUI(parseAPIDate(membership.dueAt), 'medium')}
          </Caption>
        )}
      </div>
    </div>
  )
}

function OutstandingPaymentSection({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-background-white shadow-sm border border-feedback-warning/40 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border-light bg-feedback-warning/10">
        <AlertCircle className="w-4 h-4 text-feedback-warning flex-shrink-0" />
        <h2 className="text-sm font-semibold text-text-primary m-0">Pagamento em aberto</h2>
      </div>
      <div className="p-5 space-y-6">{children}</div>
    </div>
  )
}

function OutstandingPaymentRow({
  item,
  venueName
}: {
  item: VenueMembershipOutstandingPaymentItem
  venueName: string
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Body className="text-sm font-medium text-text-primary m-0">
            {formatCycleMonth(item.cycleYear, item.cycleMonth)}
          </Body>
          <Caption className="text-text-secondary">
            {formatCurrency(item.amountCents)}
            {item.dueAt ? ` · vence ${formatDateForUI(parseAPIDate(item.dueAt), 'medium')}` : ''}
          </Caption>
        </div>
        <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-warning/20 text-feedback-warning">
          Pendente
        </span>
      </div>

      {item.paymentType === 'pixOnline' ? (
        item.pix ? (
          <div className="space-y-4 pt-1">
            <PixPayment payload={item.pix.payload} qrCodeBase64={item.pix.encodedImage} />
            <Caption className="text-text-tertiary text-center">
              Assim que o pagamento for confirmado, esta tela é atualizada automaticamente.
            </Caption>
            <div className="flex flex-col items-center gap-1 pt-3 border-t border-border-light">
              <AsaasSeal size="sm" />
              <Caption className="text-text-tertiary">Pagamento processado por Asaas IP</Caption>
            </div>
          </div>
        ) : (
          <Caption className="text-text-secondary text-center py-2">
            Estamos preparando o seu Pix. Atualize a página em instantes.
          </Caption>
        )
      ) : (
        <PayAtVenue venueName={venueName} />
      )}
    </div>
  )
}

/** Arena without online Pix: the debt is visible, the payment happens at the counter. */
function PayAtVenue({ venueName }: { venueName: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border-light bg-background-secondary/40 p-3">
      <Store className="w-5 h-5 text-text-tertiary flex-shrink-0 mt-0.5" />
      <div>
        <Body className="text-sm font-medium text-text-primary m-0">Pague na arena</Body>
        <Caption className="text-text-secondary">
          {venueName} ainda não recebe Pix pelo app. Acerte a mensalidade direto no balcão — assim que a
          arena registrar, ela some daqui.
        </Caption>
      </div>
    </div>
  )
}

function EmptyCard({ title, message, onRetry }: { title: string; message: string; onRetry?: () => void }) {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm border border-border-light">
      <H4 className="mb-2 text-text-primary">{title}</H4>
      <Body className="text-text-secondary">{message}</Body>
      {onRetry && (
        <div className="mt-4 flex justify-center">
          <Button variant={ButtonVariant.PRIMARY} onClick={onRetry}>
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  )
}
